import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftRightIcon } from 'lucide-react';
import { useV2Preview } from './useV2Shell';
import { shellPath, SIDE_LABEL } from './v2Shell';
import { v1Equivalent, v2Equivalent, writeShellView } from './shellView';

/**
 * The portal toggle in the sidebar. In the new (v2) sidebar it reads
 * "Classic portal" and goes to the current page's v1 equivalent; in the
 * classic sidebar it reads "New portal" and goes to the v2 equivalent.
 * Renders nothing for users who don't pass isV2PreviewEnabled.
 */
const ShellToggle: React.FC<{ to: 'v1' | 'v2'; isCollapsed?: boolean }> = ({ to, isCollapsed = false }) => {
  const preview = useV2Preview();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  if (!preview) return null;

  const label = to === 'v1' ? 'Classic portal' : 'New portal';
  const go = () => {
    const path = shellPath(pathname);
    writeShellView(to);
    navigate(to === 'v1' ? v1Equivalent(path, search) : v2Equivalent(path, search));
  };

  if (to === 'v1') {
    // v2 sidebar foot (the mock's `.foot` slot): hairline above, 13px, white/72.
    return (
      <div className="mt-auto flex-none border-t border-white/[.12] px-3 py-2">
        <button
          type="button"
          data-testid="shell-toggle"
          aria-label={label}
          onClick={go}
          className="flex w-full items-center gap-3 whitespace-nowrap rounded-[9px] px-[15px] py-[9px] text-left text-[13px] text-white/[.72] transition-colors hover:bg-white/[.06] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <ArrowLeftRightIcon aria-hidden="true" className="h-[18px] w-[18px] flex-none" strokeWidth={1.7} />
          <span className={SIDE_LABEL}>{label}</span>
        </button>
      </div>
    );
  }

  // Classic sidebar: same geometry as its NavItem rows.
  return (
    <button
      type="button"
      data-testid="shell-toggle"
      aria-label={label}
      title={isCollapsed ? label : undefined}
      onClick={go}
      className={`flex w-full items-center rounded-lg py-2.5 my-0.5 text-xs font-normal text-gray-400 transition-all duration-300 ease-out hover:bg-white/5 hover:text-white ${
        isCollapsed ? 'justify-center px-2' : 'px-4'
      }`}
    >
      <span className="flex w-5 flex-shrink-0 items-center justify-center">
        <ArrowLeftRightIcon aria-hidden="true" size={16} />
      </span>
      <span className={`whitespace-nowrap transition-all duration-300 ease-out ${isCollapsed ? 'ml-0 w-0 overflow-hidden opacity-0' : 'ml-3 opacity-100'}`}>
        {label}
      </span>
    </button>
  );
};

export default ShellToggle;
